import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Card, PasswordInput, Button, Text, Alert, Stack, List,
} from '@mantine/core'
import { IconAlertCircle, IconCheck, IconLock, IconShieldLock, IconWand } from '@tabler/icons-react'
import { useAuthStore } from '@/store/auth'
import { authApi } from '@/api/auth'
import { BrandLogo } from '@/components/ui/BrandLogo'
import { APP_VERSION } from '@/version'
import { interpretarErroDeTrocaDeSenha } from './changePasswordOutcome'

function validatePassword(pw: string): string[] {
  const errors: string[] = []
  if (pw.length < 10) errors.push('Mínimo 10 caracteres')
  if (!/\d/.test(pw)) errors.push('Pelo menos 1 número')
  if (!/[a-zA-Z]/.test(pw)) errors.push('Pelo menos 1 letra')
  return errors
}

const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const LOWER = 'abcdefghijklmnopqrstuvwxyz'
const DIGITS = '0123456789'
const SPECIAL = '!@#$%&*-_=+'

function generatePassword(): string {
  const all = UPPER + LOWER + DIGITS + SPECIAL
  const arr = new Uint8Array(14)
  crypto.getRandomValues(arr)
  const chars = Array.from(arr).map((b) => all[b % all.length])
  const mandatory = [
    UPPER[Math.floor(Math.random() * UPPER.length)],
    LOWER[Math.floor(Math.random() * LOWER.length)],
    DIGITS[Math.floor(Math.random() * DIGITS.length)],
    SPECIAL[Math.floor(Math.random() * SPECIAL.length)],
  ]
  mandatory.forEach((c, i) => { chars[i] = c })
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]]
  }
  return chars.join('')
}

const inputStyles = {
  label: { color: '#b6c2c9', fontWeight: 600, fontSize: 12, marginBottom: 4 },
  input: {
    backgroundColor: 'var(--ai-auth-input-bg)',
    borderColor: 'var(--ai-auth-border)',
    color: '#e9ecef',
    '&:focus': { borderColor: 'var(--ai-brand-verde)' },
  },
}

export function ChangePassword() {
  const navigate = useNavigate()
  const { logout } = useAuthStore()
  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const pwErrors = newPw ? validatePassword(newPw) : []
  const mismatch = confirmPw && newPw !== confirmPw
  const canSubmit = currentPw && newPw && confirmPw && !mismatch && pwErrors.length === 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setError('')
    setLoading(true)
    try {
      await authApi.changePassword(currentPw, newPw)
      await logout()
      navigate('/login')
    } catch (err: any) {
      const r = interpretarErroDeTrocaDeSenha(err?.response?.data?.detail)
      if (r.tipo === 'ja_trocada') {
        // A senha JA esta trocada — mesmo desfecho do sucesso: sair para o
        // login com a senha nova, em vez de pedir para tentar de novo.
        await logout()
        navigate('/login')
      } else {
        setError(r.mensagem)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'var(--ai-auth-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle ambient glow */}
      <Box
        style={{
          position: 'absolute',
          top: '-30%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(9, 141, 147, 0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <Box style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
        {/* Brand */}
        <Stack align="center" mb="xl" gap={4}>
          <BrandLogo variant="horizontal" color="white" width={180} />
          <Text size="xs" c="rgba(255,255,255,0.35)" mt={8} fw={600} tt="uppercase" style={{ letterSpacing: '0.08em' }}>
            Troca de Senha Obrigatória
          </Text>
        </Stack>

        {/* Change password card */}
        <Card
          radius="md"
          p="xl"
          style={{
            backgroundColor: 'var(--ai-auth-card-bg)',
            border: '1px solid var(--ai-auth-border)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.3)',
          }}
        >
          <Box style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <IconShieldLock size={22} color="#098d93" />
            <Text fw={700} size="lg" c="#e9ecef">Escolha sua nova senha</Text>
          </Box>
          <Text size="xs" c="#6c757d" mb="lg">
            Sua conta foi criada com uma senha temporária. Escolha uma senha definitiva para continuar.
          </Text>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md" radius="sm"
              styles={{ root: { backgroundColor: 'rgba(255, 91, 87, 0.1)', border: '1px solid rgba(255, 91, 87, 0.2)' } }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack gap="sm">
              <PasswordInput
                label="Senha atual (temporária)"
                placeholder="Digite a senha recebida por email"
                value={currentPw}
                onChange={e => setCurrentPw(e.currentTarget.value)}
                leftSection={<IconLock size={16} />}
                required
                size="md"
                styles={inputStyles}
                data-sensitive="true"
                wrapperProps={{ 'data-sensitive': 'true' }}
              />
              <PasswordInput
                label="Nova senha"
                placeholder="Escolha sua nova senha"
                value={newPw}
                onChange={e => { setNewPw(e.currentTarget.value); setConfirmPw('') }}
                leftSection={<IconLock size={16} />}
                required
                size="md"
                styles={inputStyles}
                error={newPw && pwErrors.length > 0 ? ' ' : undefined}
                data-sensitive="true"
                wrapperProps={{ 'data-sensitive': 'true' }}
              />
              <Button
                variant="subtle"
                size="compact-xs"
                color="teal"
                leftSection={<IconWand size={13} />}
                onClick={() => {
                  const pw = generatePassword()
                  setNewPw(pw)
                  setConfirmPw(pw)
                }}
                style={{ alignSelf: 'flex-start', marginTop: -4, fontSize: 11, fontWeight: 600 }}
              >
                Sugerir senha forte
              </Button>
              {newPw && pwErrors.length > 0 && (
                <List size="xs" spacing={2}
                  styles={{ item: { color: '#ff6b6b', fontSize: 11 } }}
                >
                  {pwErrors.map(err => <List.Item key={err}>{err}</List.Item>)}
                </List>
              )}
              {newPw && pwErrors.length === 0 && (
                <Text size="xs" c="#098d93" fw={600}>
                  <IconCheck size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Senha valida
                </Text>
              )}
              <PasswordInput
                label="Confirmar nova senha"
                placeholder="Repita a nova senha"
                value={confirmPw}
                onChange={e => setConfirmPw(e.currentTarget.value)}
                leftSection={<IconLock size={16} />}
                required
                size="md"
                styles={inputStyles}
                error={mismatch ? 'As senhas não coincidem' : undefined}
                data-sensitive="true"
                wrapperProps={{ 'data-sensitive': 'true' }}
              />
              <Button
                type="submit"
                fullWidth
                loading={loading}
                disabled={!canSubmit}
                mt={8}
                size="md"
                radius="sm"
                style={{
                  backgroundColor: canSubmit ? 'var(--ai-brand-verde)' : undefined,
                  border: 'none',
                  fontWeight: 700,
                  fontSize: 13,
                  letterSpacing: '0.02em',
                  transition: 'all var(--ai-transition-base)',
                }}
                onMouseEnter={(e) => {
                  if (canSubmit) e.currentTarget.style.backgroundColor = '#077a80'
                }}
                onMouseLeave={(e) => {
                  if (canSubmit) e.currentTarget.style.backgroundColor = 'var(--ai-brand-verde)'
                }}
              >
                Trocar Senha
              </Button>
            </Stack>
          </form>

          <Text size="xs" c="#51585e" ta="center" mt="xl" fw={600}>
            ConsoleAI v{APP_VERSION}
          </Text>
        </Card>

        <Text size="xs" c="rgba(255,255,255,0.2)" ta="center" mt="md" fw={500}>
          &copy; 2025-2026 Amazonas Inovare. All rights reserved.
        </Text>
      </Box>
    </Box>
  )
}
